import { useCollapsedSidebarLinksV3 } from "@circle-react-shared/SettingsNav/CollapsedSidebar/useCollapsedSidebarLinksV3";
import { useIsCircleAiBotsEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import { useIsPlusCommandCenterEnabled } from "@circle-react/components/SettingsApp/PlusCommandCenter/useIsPlusCommandCenterEnabled";
import { useIsCommunityAdmin } from "@circle-react/contexts/punditUserContext";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { adminDashboardRoutes } from "@circle-react/lib/routes/adminDashboard";
import { liveStreamsRoutes } from "@circle-react/lib/routes/liveStreams";
import { siteRoutes } from "@circle-react/lib/routes/site";
import { workflowsRoutes } from "@circle-react/lib/routes/workflows";

import { t } from "@/i18n-js/instance";
import { useIsAvatarsV2Layout } from "@/react/components/Avatars/useIsAvatarsV2Layout";
import {
  BOTS_HISTORY_PATH,
  BOTS_LIST_PATH,
} from "@/react/pages/CircleAi/Bots/constants";

import type { SidebarLinkPathKey } from "../../shared/SettingsNav/CollapsedSidebar/types";
import { DASHBOARD_BUILD_ITEM_ID } from "./buildItemIds";
import { buildSettingsNavItems } from "./buildSettingsNavItems";
import type {
  BuildNavItem,
  BuildNavSubItem,
  BuildSectionSpec,
  ResolveContext,
} from "./types";
import { useSettingsNavLinks } from "./useSettingsNavLinks";
import { buildSettingsNavSubItems } from "./useSettingsNavSubItems";

const BUILD_SECTIONS: BuildSectionSpec[] = [
  {
    key: "members",
    compassIcon: "group",
    compassActiveIcon: "group-filled",
    label: () => t("circle_ai_dock.build.audience"),
    childLabels: {
      [settingsNavPathV3.members.manage]: () =>
        t("circle_ai_dock.build.manage_audience"),
    },
  },
  {
    key: "email",
    compassIcon: "paper-plane",
    compassActiveIcon: "paper-plane-filled",
  },
  {
    key: "newsletter",
    compassIcon: "email",
    compassActiveIcon: "email-filled",
  },
  {
    key: "content",
    compassIcon: "page",
    compassActiveIcon: "page-filled",
    label: () => t("circle_ai_dock.build.community"),
    childOrder: [
      settingsNavPathV3.content.posts,
      settingsNavPathV3.content.spaces,
      liveStreamsRoutes.index(),
      settingsNavPathV3.content.files,
      settingsNavPathV3.content.topics,
      settingsNavPathV3.content.moderation,
    ],
    childLabels: {
      [liveStreamsRoutes.index()]: () => t("circle_ai_dock.build.live_streams"),
    },
  },
  {
    key: "events",
    compassIcon: "ticket",
    compassActiveIcon: "ticket-filled",
  },
  {
    key: "courses",
    compassIcon: "graduate-cap",
    compassActiveIcon: "graduate-cap-filled",
  },
  {
    key: "workflows",
    compassIcon: "zap",
    compassActiveIcon: "zap-filled",
    childLabels: {
      [workflowsRoutes.list()]: () => t("circle_ai_dock.build.workflows"),
    },
  },
  {
    key: "community_bot",
    compassIcon: "sparkle-box",
    compassActiveIcon: "sparkle-box-filled",
    label: () => t("circle_ai_dock.build.agents"),
    path: settingsNavPathV3.community_bot.agents,
    childOrder: [
      settingsNavPathV3.community_bot.agents,
      settingsNavPathV3.community_bot.knowledge,
    ],
    isEnabled: ({ isAvatarsEnabled }) => !isAvatarsEnabled,
  },
  {
    key: "community_bot",
    compassIcon: "imagine",
    compassActiveIcon: "imagine-filled",
    label: () => t("circle_ai_dock.build.avatars"),
    path: settingsNavPathV3.community_bot.agents,
    children: () => [],
    isEnabled: ({ isAvatarsEnabled }) => isAvatarsEnabled,
    section: "agents",
  },
  {
    key: "bots",
    linkKey: "community_bot",
    compassIcon: "sparkle-box",
    compassActiveIcon: "sparkle-box-filled",
    label: () => t("circle_ai_dock.build.bots"),
    path: BOTS_LIST_PATH,
    activePaths: [BOTS_LIST_PATH],
    children: () => [
      {
        id: "bots-overview",
        label: t("circle_ai_dock.build.bots"),
        to: BOTS_LIST_PATH,
        exact: true,
      },
      {
        id: "bots-history",
        label: t("circle_ai_dock.build.history"),
        to: BOTS_HISTORY_PATH,
      },
    ],
    isEnabled: ({ isBotsEnabled }) => isBotsEnabled,
    section: "agents",
  },
  {
    key: "site",
    compassIcon: "window",
    compassActiveIcon: "window-filled",
    label: () => t("website"),
    // Website has no standalone landing page — clicking it opens Pages.
    path: settingsNavPathV3.content.pages,
    children: ({ subItemsByPathKey }) => {
      const byPath = new Map(
        [
          ...(subItemsByPathKey.content ?? []),
          ...(subItemsByPathKey.site ?? []),
        ].map(child => [child.to, child]),
      );
      return [
        settingsNavPathV3.content.pages,
        siteRoutes.navigation(),
        siteRoutes.codeSnippets(),
      ]
        .map(path => byPath.get(path))
        .filter((child): child is BuildNavSubItem => Boolean(child));
    },
  },
  {
    key: "paywalls",
    compassIcon: "dollar",
    compassActiveIcon: "dollar-filled",
    label: () => t("circle_ai_dock.build.payments"),
    children: ({ fromSettingsNav, isAffiliatesEnabled }) =>
      isAffiliatesEnabled
        ? fromSettingsNav("paywalls", "affiliates")
        : fromSettingsNav("paywalls"),
  },
  {
    key: "analytics_v2",
    compassIcon: "chart-square",
    compassActiveIcon: "chart-square-filled",
  },
  {
    key: "plus",
    compassIcon: "phone",
    compassActiveIcon: "phone-filled",
    label: () => t("circle_ai_dock.build.branded_app"),
    childLabels: {
      [settingsNavPathV3.plus.landing_page]: () =>
        t("circle_ai_dock.build.branded_app_builder"),
      [settingsNavPathV3.plus.index]: () =>
        t("circle_ai_dock.build.branded_app_builder"),
    },
  },
];

const applyChildOverrides = (
  spec: BuildSectionSpec,
  items: BuildNavSubItem[],
  isPlusCommandCenterEnabled: boolean,
): BuildNavSubItem[] => {
  const labelled = items.map(child => {
    const label =
      isPlusCommandCenterEnabled &&
      spec.key === "plus" &&
      child.to === settingsNavPathV3.plus.index
        ? () => t("settings_nav_v3.branded_apps_dashboard.command_center")
        : spec.childLabels?.[child.to];
    return label ? { ...child, label: label() } : child;
  });
  if (!spec.childOrder) return labelled;

  const pinned = spec.childOrder.flatMap(to =>
    labelled.filter(child => child.to === to),
  );
  const rest = labelled.filter(child => !spec.childOrder?.includes(child.to));
  return [...pinned, ...rest];
};

export const useBuildNavItems = (): BuildNavItem[] => {
  const isAdmin = useIsCommunityAdmin();
  const isPlusCommandCenterEnabled = useIsPlusCommandCenterEnabled();
  const isAvatarsEnabled = useIsAvatarsV2Layout();
  const isBotsEnabled = useIsCircleAiBotsEnabled();
  const isAgentsSectionEnabled = isAvatarsEnabled && isBotsEnabled;
  const links = useCollapsedSidebarLinksV3();
  const settingsNavLinks = useSettingsNavLinks();
  const subItemsByPathKey = buildSettingsNavSubItems(settingsNavLinks);
  const settingsItems = buildSettingsNavItems(settingsNavLinks);

  const settingsOwnedPaths = settingsItems.flatMap(group =>
    (group.children ?? []).map(child => child.to),
  );

  // Keep moderation under Build → Community for moderators (and admins),
  // rather than burying it only in Settings groups.
  const excludedPaths = new Set<string>(
    [...settingsOwnedPaths, settingsNavPathV3.content.pages].filter(
      path => path !== settingsNavPathV3.content.moderation,
    ),
  );

  const ctx: ResolveContext = {
    fromSettingsNav: (...pathKeys) =>
      pathKeys
        .flatMap(pathKey => subItemsByPathKey[pathKey] ?? [])
        .filter(child => !excludedPaths.has(child.to)),
    isAffiliatesEnabled: links.some(
      link => link.pathKey === "affiliates" && link.enabled,
    ),
    isAvatarsEnabled,
    isBotsEnabled,
    subItemsByPathKey,
  };

  const linkByPathKey = new Map(
    links.filter(link => link.enabled).map(link => [link.pathKey, link]),
  );

  const sectionItems = BUILD_SECTIONS.flatMap<BuildNavItem>(spec => {
    const link = linkByPathKey.get(
      spec.linkKey ?? (spec.key as SidebarLinkPathKey),
    );
    if (!link || spec.isEnabled?.(ctx) === false) return [];

    const children = applyChildOverrides(
      spec,
      spec.children?.(ctx) ?? ctx.fromSettingsNav(spec.key),
      isPlusCommandCenterEnabled,
    );

    const path =
      spec.key === "content" && !isAdmin
        ? settingsNavPathV3.content.posts
        : (spec.path ?? link.path);

    return [
      {
        id: spec.key,
        label: spec.label?.() ?? link.label,
        icon: link.icon,
        compassIcon: spec.compassIcon,
        compassActiveIcon: spec.compassActiveIcon,
        activePaths: spec.activePaths,
        path,
        ...(spec.section && isAgentsSectionEnabled
          ? { section: spec.section }
          : {}),
        ...(children.length ? { children } : {}),
      },
    ];
  });

  if (!isAdmin) {
    return sectionItems;
  }

  const dashboardGroup: BuildNavItem = {
    id: DASHBOARD_BUILD_ITEM_ID,
    label: t("admin_nav_v4.dashboard"),
    icon: "dashboard",
    compassIcon: "compass",
    compassActiveIcon: "compass-filled",
    path: adminDashboardRoutes.index(),
    exact: true,
  };

  return [dashboardGroup, ...sectionItems];
};
